import { createContext, type ReactNode, useMemo } from 'react';

import { useEndpoint } from 'api';
import { useRequiredContext } from 'hooks';
import type {
	ContactRole,
	CustomerAddress,
	CustomerContactId,
	CustomerId,
	CustomerIdentificationNumber,
} from 'models/api';
import type { CustomerType, VoyadoId } from 'types';
import { is } from 'utils/helpers';

interface UserInfo {
	/** Company name for Pro users, full name, firstname lastname concatenated, for Club users */
	companyName: string | undefined;
	contactRole: ContactRole | undefined;
	customerId: CustomerId | undefined;
	customerIdentificationNumber: CustomerIdentificationNumber | undefined;
	customerType: CustomerType | undefined;
	email: string | undefined;
	error: boolean;
	firstName: string | undefined;
	/** Full name for Club users, first name and last name concatenated, Company name for Pro users */
	fullName: string | undefined;
	invalidateContactInformationCache: () => void;
	invalidateCustomerInformationCache: () => void;
	invoiceAddress: CustomerAddress | undefined;
	isAdmin: boolean;
	isLoading: boolean;
	isPro: boolean;
	lastName: string | undefined;
	loginMethod: string | undefined;
	phoneNumber: string | undefined;
	phoneNumberNoSpaces: string | undefined;
	postalAddress: CustomerAddress | undefined;
	voyadoContactId: VoyadoId | undefined;
}
const UserContext = createContext<UserInfo | undefined>(undefined);

export interface JWTUserInfo {
	customerContactId?: CustomerContactId;
	customerExtVoyado?: VoyadoId;
	customerType?: CustomerType;
	loginMethod?: string;
}

interface Props {
	children: ReactNode;
	jwtUserInfo: JWTUserInfo | undefined;
}

export function UserContextProvider({ children, jwtUserInfo }: Props) {
	const customerType: CustomerType = jwtUserInfo?.customerType ?? 'Anonymous';
	const {
		data: customerInformation,
		error: errorCustomerInformation,
		invalidateCache: invalidateCustomerInformationCache,
		isLoading: isLoadingCustomerInformation,
	} = useEndpoint('GetCustomer', {
		isActive: is.oneOf(customerType, 'Pro', 'Club', 'Staff'),
	});

	const isPro = customerType === 'Pro';

	const {
		data: contact,
		error: errorContact,
		invalidateCache: invalidateContactInformationCache,
		isLoading: isLoadingContact,
	} = useEndpoint(
		jwtUserInfo?.customerContactId && isPro
			? {
					name: 'GetContact',
					pathParams: { customerContactId: jwtUserInfo?.customerContactId },
				}
			: null,
	);

	const phoneNumber = isPro
		? contact?.phoneNumber.default
		: customerInformation?.cellPhoneNumberFormatted?.default;
	const email = isPro ? contact?.email : customerInformation?.emailAddress;

	const value = useMemo(
		() => ({
			companyName: isPro ? customerInformation?.fullName : undefined,
			contactName: contact?.name,
			contactRole: contact?.role,
			customerId: customerInformation?.id,
			customerIdentificationNumber: customerInformation?.identificationNumber,
			customerType,
			email,
			error: Boolean(errorContact || errorCustomerInformation),
			firstName: customerInformation?.firstName ?? contact?.firstName,
			fullName: customerInformation?.fullName,
			invalidateContactInformationCache,
			invalidateCustomerInformationCache,
			invoiceAddress: customerInformation?.invoiceAddress,
			isAdmin: contact?.role === 'Admin',
			isLoading: isLoadingContact || isLoadingCustomerInformation,
			isPro,
			lastName: customerInformation?.lastName ?? contact?.lastName,
			loginMethod: jwtUserInfo?.loginMethod ?? '',
			phoneNumber,
			phoneNumberNoSpaces: phoneNumber?.replaceAll(/[\s,-]/g, ''),
			postalAddress: customerInformation?.postalAddress,
			voyadoContactId:
				jwtUserInfo?.customerExtVoyado ?? customerInformation?.voyadoContactId,
		}),
		[
			contact?.firstName,
			contact?.lastName,
			contact?.name,
			contact?.role,
			customerInformation?.firstName,
			customerInformation?.fullName,
			customerInformation?.id,
			customerInformation?.identificationNumber,
			customerInformation?.invoiceAddress,
			customerInformation?.lastName,
			customerInformation?.postalAddress,
			customerInformation?.voyadoContactId,
			customerType,
			email,
			errorContact,
			errorCustomerInformation,
			invalidateContactInformationCache,
			invalidateCustomerInformationCache,
			isLoadingContact,
			isLoadingCustomerInformation,
			isPro,
			jwtUserInfo?.customerExtVoyado,
			jwtUserInfo?.loginMethod,
			phoneNumber,
		],
	);

	return <UserContext value={value}>{children}</UserContext>;
}

UserContextProvider.displayName = 'UserContextProvider';

export function useUserContext() {
	return useRequiredContext(UserContext, 'UserContext');
}
